import {
  ChoiceId,
  PollyId,
  PollyUserId,
  QuestionId,
  QuestionType as PlatformQuestionType,
  PollyUserId as PlatformUserId,
  GuestUserId as PlatformGuestId,
  ResponseUserType as PlatformUserType,
  QRCodeRoutingType,
  ShortCodeId,
  SeriesId,
  SlackChannelTargetResults,
  OpenEndedQuestion,
  NominalQuestion,
  OrdinalQuestion,
  PollyState,
  SurveyAnonymity,
  QuizMode,
  ISO8601Duration,
  LivePollyProgressionMode,
  QuizResultsSettings as IQuizResultsSettings,
  RoutingMetadataId,
  JoinCodeState,
} from "@pollyai/types";
import {
  AnonymityLevel,
  Cadence,
  DurationUnit,
  ResultsDeliveryType,
} from "@pollyslack/types/src/common";
import {
  SubTemplateCategory,
  TemplateCategory as TemplateCategoryType,
} from "@pollyslack/types/src/templates";

import JSXSlack from "jsx-slack";
import { QuestionnaireWeightId } from "./core/types";
import { OrgSubscriptionSchema as OrgSubscription } from "./features/types";
import { QuestionType } from "./schemas/questions/questionTypes";
import { GlobalTemplateModalHeaders } from "./schemas/userFacing/GlobalTemplates";
import {
  PlatformPollySchema,
  PlatformPollyWebLinkTargetSchema,
} from "./platform";
import { Resource } from "./pacl";
import { PollySharingSettingsSchema } from "@pollyai/api-client-server";

/*
Types used throughout all of the projects managed by Polly.

Comments use the following key terminology:
FOREIGN KEY: This field is used as a foreign key into another collection in this file
PLATFORM_KEY: This field is a key for an object in one of the supported platforms e.g. Slack
DEPRECATED: Used for functionality that is no longer supported or needed. On the way out, but
            may still be used by old/dead code.
LEGACY: Used for old functionality, but still in use by older teams and thus not eligible for
        removal. Revisit deprecation every so often.
*/
export { LifecycleEvent } from "./lifecycleEvents";

// Types to facilitate passing around a logging function when necessary
export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogFunction = (logLevel: LogLevel, text: string) => void;

// Google sheet integration related polly types
export type PollysForGoogleSheetIntegration =
  | "track"
  | "poll"
  | "survey"
  | "workflow";

export enum Platform {
  // Email = 0, // commenting out so switch statements don't have to handle until implemented
  Slack = 1,
  // Msteams = 2,
  Hangouts = 3,
  // Stride = 4,
}

// Beginning to use PollType as an _id for questionTemplates. Name will need to change eventually to reflect this.
export enum PollType {
  ONE_TO_FIVE = "1to5",
  ONE_TO_TEN = "1to10",
  FIVE_POINT = "fivePoint",
  SEVEN_POINT = "sevenPoint",
  VERBAL_BINARY = "verbalBinary",
  NPS = "nps",
  OPEN_ENDED = "openEnded",
  ALLOCATION = "allocation",
  RANKED = "ranked",
  RANKED_WEIGHTED_VOTING = "rankedWeightedVoting",
  RANK_FIVE_EMOJI = "rankFiveEmoji",

  // Disabled (PS-1557)
  IMAGE = "image",
  NOMINAL_IMAGE = "nominalImage",
}

// An array of the poll types in the usual order we present them to users
export const POLL_TYPES = [
  PollType.ONE_TO_FIVE,
  PollType.ONE_TO_TEN,
  PollType.FIVE_POINT,
  PollType.VERBAL_BINARY,
  PollType.NPS,
  PollType.OPEN_ENDED,
  PollType.ALLOCATION,
  PollType.RANKED,
  PollType.RANKED_WEIGHTED_VOTING,
  PollType.RANK_FIVE_EMOJI,

  // Disabled (PS-1557)
  PollType.IMAGE,
];

export enum PollyType {
  poll = "poll",
  survey = "survey",
  hotTake = "hot take",
  triviaGame = "trivia game",
  teamBattleTrivia = "team battle trivia",
  awardsGame = "awards game",
  standup = "standup",
  suggestionbox = "suggestion box",
  qna = "qanda",
  wordCloud = "word cloud",
  pulseSurvey = "pulse survey",
  quiz = "quiz",
  icebreaker = "icebreaker",
  yesNo = "yes/no",
}

/**
 * Format type for special poll formats
 * Currently only used for icebreakers
 */
export enum FormatType {
  icebreaker = "icebreaker",
  hotTake = "hot-take",
  yesNo = "yes-no",
}

//Combine PollyType and PollType
export const AllPollyTypes = {
  ...PollyType,
  ...PollType,
  nominalImage: PollType.NOMINAL_IMAGE,
} as const;

export type AllPollyTypes = (typeof AllPollyTypes)[keyof typeof AllPollyTypes];

export const QUESTION_TYPES_WITH_WORDCLOUD = [
  PollType.VERBAL_BINARY,
  PollType.OPEN_ENDED,
  PollType.RANK_FIVE_EMOJI,
  "wordCloud",
  PollType.FIVE_POINT,
  PollType.ONE_TO_FIVE,
  PollType.ONE_TO_TEN,
  PollType.NPS,
  PollType.ALLOCATION,
  PollType.RANKED,
  PollType.RANKED_WEIGHTED_VOTING,
  PollType.IMAGE,
  "nominalImage",
];

export enum QuizStyle {
  CLASSIC = "classic",
  LEARNING = "learning",
  TRIVIA = "trivia",
  CUSTOM = "custom",
}

export enum QuizModeEnum {
  LIVE = "live",
  STATIC = "static",
}

// The name Duration is already used in a bunch of libraries, so name it PollyDuration to avoid confusion
export interface PollyDuration {
  unit: DurationUnit;
  value: number;
}

// An array of the cadences in the usual order we present them to users
export const CADENCES = [
  Cadence.DAILY_INCLUDING_WEEKENDS,
  Cadence.DAILY,
  Cadence.WEEKLY,
  Cadence.BIWEEKLY,
  Cadence.TRIWEEKLY,
  Cadence.MONTHLY,
  Cadence.THREE_MONTHS,
  Cadence.BIANNUALLY,
];

export type Frequency = "one-time" | "scheduled-one-time" | Cadence;
// Same as above, but with null representing "one-time" (should eliminate this in the future)
export type NullableFrequency = null | "scheduled-one-time" | Cadence;
// An array of the frequencies in the usual order we present them to users
export const FREQUENCIES: Frequency[] = [
  "one-time",
  "scheduled-one-time",
  Cadence.DAILY_INCLUDING_WEEKENDS,
  Cadence.DAILY,
  Cadence.WEEKLY,
  Cadence.BIWEEKLY,
  Cadence.TRIWEEKLY,
  Cadence.MONTHLY,
  Cadence.THREE_MONTHS,
  Cadence.BIANNUALLY,
];

export const enum Audience {
  PRIVATE,
  ORGANIZATION,
  SPECIFIED_USERS,
  COMMUNITY,
}

export const enum Link {
  NONE,
  SUBSCRIPTION,
  RESULTSLINKEDTOPARENT,
  BOTH,
}

export type OfficialMessageContext = {
  iconImageSrc: string;
};

export interface RateLimitingInfo {
  executedAt: Date;
  nextScheduledFor?: Date;
}

export interface Organization {
  _id: string;
  createDate: Date;
  uninstalledDate?: Date;

  // Friendly name
  name?: string;

  // Team URL. For Slack: https://xxxx.slack.com
  domain?: string;

  // FOREIGN KEY: User. An array of Meteor user IDs that has extra permissions
  // for the organization. Currently only used in email/Slack orgs.
  orgAdmin?: string[];

  // A platform independent way of knowing whether or not a team is still able to use Polly
  // Marked as optional until it's fully plumbed in across all the components of Polly
  active?: boolean;

  // When the org should be synced next.  Currently only syncing slack orgs, but if we want
  // periodic sync for other platforms eventually, we can use this property for scheduling them too.
  nextSyncDate?: Date;

  // FOREIGN KEY: Enterprise grid ID
  enterpriseGridId?: string;

  settings: {
    platform: Platform;
    subscription?: OrgSubscription;
    setupComplete?: boolean;

    // The name that the org has picked for the bot, which can be customized in settings
    slackBotName?: string;
    // The bot's user id in this workspace
    slackBotUserId?: string;

    // The API token to do all the things in Slack
    slackBotToken?: string;
    encryptedSlackBotToken?: string;

    // The API token for the user that installed the bot in Slack. It is never used, but
    // stored just in case there's a use case that requires it at some point
    slackAdminToken?: string;
    encryptedSlackAdminToken?: string;

    slackTeamId?: string;
    slackEnterpriseId?: string;
    slackSlashTokenOverride?: boolean;

    // Whether or not the Slack API token is still active
    slackBotTokenActive?: boolean;

    // LEGACY. This field is still used by some old teams (2015) that installed custom slash tokens
    // before Slack allowed install via OAuth. Used as the mechanism for auth in this case instead
    // of the SLACK_SLASH_TOKEN
    slackSlashToken?: string;

    // Slack property which stores whether the team allows message deletion by users
    allow_message_deletion?: boolean;

    // LEGACY. Slack token used by the team if they installed during the legacy bot token era
    // which was basically any team that installed on Slack prior to January 2016
    // (Slack calls this a Custom Bot Token)
    legacyBotToken?: boolean;

    // A rough count of users and when it was last counted
    cachedUserCount?: {
      date: Date;
      userCount: number;
      userCountByType?: {
        primary_owner: number;
        enterprise_owner: number;
        enterprise_admin: number;
        owner: number;
        admin: number;
        ultra_restricted: number;
        restricted: number;
        member: number;
      };
    };

    // A rough count of users, only assigned value for Admin app view model
    cachedBillableUserCount?: number;

    // Slack team email_domain property (e.g. "polly.ai") or "none" if it was absent
    email_domain?: string;
    // Which paid plan a Slack team is on (if any). Usually one of the following strings:
    // none, std, plus, enterprise
    plan?: string;

    dataRetentionPolicy?: {
      deleteAfterUninstall: boolean; // data is deleted 30 days after uninstallation
      retainFor: PollyDuration; // data is automatically deleted after this duration
    };

    // Icon for the organization.  Typically this is a url synced from the Slack team.info result
    iconImage?: string;

    // Scopes that the organization's bot token has
    scopes?: string[];

    // Organizational opt-in flags (marketing/lifecycle)
    optIns?: {
      marketing?: boolean;
    };

    // Whether org admin has enabled User Groups collaboration
    userGroupsCollaborationEnabled?: boolean;

    // Whether MCP (Model Context Protocol) is enabled for the org
    mcpEnabled?: boolean;

    // Whether members can use Polly through Slackbot's MCP client.
    // Requires mcpEnabled.
    slackbotMcpEnabled?: boolean;
  };
}

export interface Brief {
  _id: string;
  createdAt: Date;
  platform: Platform;
  targetUser: string;
  assignDate: Date;
  orgId: string; // FOREIGN KEY: Organization
  briefGroupId: string; // FOREIGN KEY: Organization
  pollQuestions: string[]; // FOREIGN KEY: Post
  teamTags: string[];
  userName?: string;
  dmChannel?: string; // PLATFORM KEY: IM channel ID
  timestamps?: string[]; // PLATFORM KEY: IM channel timestamps of poll messages
  resultsTimestamp?: string; // PLATFORM KEY: IM channel timestamps of results messages
  postInfo?: BriefPostInfo[];
  skip?: boolean;
  deleted: boolean;
  slackRateLimitingInfo?: RateLimitingInfo;

  // DEPRECATED: reminders are no longer separate messages, instead the polls are deleted and resent
  // PLATFORM KEY: IM channel ID/timestamps of reminder messages
  reminderMessageData?: { channel: string; timestamp: string };
}

export interface BriefPostInfo {
  _id: string; // FOREIGN KEY: Post
  voted?: boolean;
  timestamp?: string;
  commentAdded?: boolean;
  viewComments?: boolean;
  viewAllResponses?: boolean;
  mappingCache?: { vote: number }[];
}

export interface BriefGroup {
  _id: string;
  createdAt: Date;
  updatedAt?: Date;
  afterUpdate?: {
    notifyVoters?: boolean;
    invalidateResponses?: boolean;
  };
  platform: Platform;
  title: string;
  anonymityLevel: AnonymityLevel;
  appeal?: string;
  orgId: string; // FOREIGN KEY: Organization
  assignDate: Date;
  originUserId?: string;
  originUserName?: string;
  channel?: string; // Slack/Teams id of the channel, for in-channel polls which have been sent out
  timestamp?: string;
  reminderCount?: number;
  reminderFrequency?: number;
  reminderFrequencyUnit?: DurationUnit;
  nextRemindDate?: Date;
  command: string;
  trackId?: string; // FOREIGN KEY: Track
  strictVoting: boolean;
  sent: boolean;
  deleted: boolean;
  shared: boolean; // allow access on web app to anyone with the link
  /** If true, platform mints a join code for this poll so people can join via a shared code. Follows the web link's auth. */
  joinCodeEnabled?: boolean;
  results: ResultsDeliveryType;
  resultsDelivered?: Date;
  orgTemplateId?: string; // FOREIGN KEY: OrgTemplate
  globalTemplateId?: string; // FOREIGN KEY: GlobalTemplate
  resultsUpdatesRateLimitingInfo?: RateLimitingInfo;
  appearAs?: string;
  customMetadata?: CustomMetadata;
  /** Foreign key reference to workflows collection */
  workflowId?: string;
  /** Foreign key reference to workflows.action sub object. specifies a single action within a workflow */
  workflowActionId?: string;
  workflowEventId?: string; // FOREIGN KEY: WorkflowEvent

  /** DEPRECATED: No objects in database with this field */
  isSurvey?: boolean;
  /** DEPRECATED: reminders are no longer separate messages, instead the polls are deleted and resent */
  // PLATFORM KEY: IM channel ID/timestamps of reminder messages
  reminderMessageData?: { channel: string; timestamp: string };
  lastReminderSentAt?: Date[];
  postResultsOnClose?: boolean;
  slackWorkflowConfigurationId?: string; // FOREIGN KEY: SlackWorkflowConfiguration
  /** Integration with google sheets. If set, this Poll should dump live results to this sheet id */
  googleSheetId?: string;
  /** Metadata for google sheet integration */
  googleSheetMetadata?: GoogleSheetMetadata;
  /** Foreign key reference to questionnaire weights collection */
  questionnaireWeightId?: QuestionnaireWeightId;

  /** Only used to identify auto-generated demo polls */
  scriptGenerationId?: string;

  /** Only used for official pollys - omitting few props as they won't be stored in DB but needed otherwise */
  officialMessageContext?: OfficialMessageContext;
}

/** Slack host message address where a web polly URL was unfurled (for fan-out refresh). */
export interface PollyUnfurlPlacement {
  _id?: string;
  briefGroupId: string;
  orgId: string;
  slackTeamId: string;
  channel: string;
  hostMessageTs: string;
  updatedAt: Date;
}

export interface ChoicePlatformMetadata {
  platformChoiceId: ChoiceId;

  // Legacy properties
  emoji: string;
}

export interface QuestionPlatformMetadata {
  questionId: string;
  platformQuestionId: QuestionId;
  platformQuestionType: PlatformQuestionType;
  choiceMetadata: ChoicePlatformMetadata[];

  // Legacy question properties
  pollType: PollType;
  trackPollId?: string; // question level track reference
  currentMapping: string; // incrementally generated anon ids
  queuePosition?: number;
  multilineInput?: boolean;
  customMetadata?: CustomMetadata;
  wordCloudImageCachedUrl?: string;
}

// Platform metadata for a response
export interface ResponsePlatformMetadata {
  _id: string;
  platformPollyId: PollyId;
  createdAt: Date;
  updatedAt?: Date;
  platformUserId: PlatformUserId | PlatformGuestId;
  platformUserType: PlatformUserType;
  platformUserDisplayName: string;
  platformUserEmail?: string;
}

// Platform metadata for a Polly
export interface PollyPlatformMetadata {
  _id: string;
  createdAt: Date;
  updatedAt?: Date;
  briefGroupId?: string;
  surveyId?: string;
  platformPollyId: PollyId;
  webVoteCode?: string;
  /** Whether a join code is enabled for this polly (mirrors the platform sharing setting) */
  joinCodeEnabled?: boolean;
  /** The platform-generated join code (code + state), read back from platform for display */
  joinCode?: JoinCode;
  questionMetadata: QuestionPlatformMetadata[];
  isImagePoll?: boolean;

  // Legacy polly properties
  timestamp: string;
  reminderCount?: number;
  reminderFrequency?: number;
  reminderFrequencyUnit?: DurationUnit;
  nextRemindDate?: Date; // Only used for polls, stored on SurveyInstance for surveys
  reminderMessageData?: { channel: string; timestamp: string }; // Only used for polls, stored on SurveyInstance for surveys
  command: string;
  shared: boolean;
  results: ResultsDeliveryType;
  resultsDelivered?: Date;
  globalTemplateId?: string;
  resultsUpdatesRateLimitingInfo?: RateLimitingInfo; // Only used for polls, stored on SurveyInstance for surveys
  workflowId?: string;
  workflowActionId?: string;
  workflowEventId?: string;
  postResultsOnClose?: boolean;
  slackWorkflowConfigurationId?: string;
  googleSheetId?: string;
  scriptGenerationId?: string;
  officialMessageContext?: OfficialMessageContext;
  isLimited: boolean;
  sent: boolean;
  orgId: string;
  anonymityLevel: AnonymityLevel;
  teamId?: string[];
  deleted?: boolean;
  customMetadata?: CustomMetadata;

  responseMigrationWriteCounter?: number;
}

export interface SlackPoll {
  _id: string;
  createdAt: Date;
  updatedAt?: Date;
  org: string; // FOREIGN KEY: Organization
  domain?: string;
  trialEndDate?: Date;
  user: string;
  userName: string;
  channel: string;
  channelName: string;
  expiry: Date;
  command: string;
  text: string;
  tz?: string;
  enterprise_id?: string;
}

export type DeliveryState = "pending" | "in-progress" | "delivered";

export interface GoogleSheetMetadata {
  dataSyncInterval?: GSheetSyncInterval;
  nextSyncTime?: Date;
}

export interface Survey {
  _id: string;
  createdAt: Date;
  afterUpdate?: {
    notifyVoters?: boolean;
    invalidateResponses?: boolean;
  };
  orgId: string; // FOREIGN KEY: Organization
  teamId: string[]; // FOREIGN KEY: Team
  title: string;
  appeal?: string;
  dmAppeal?: boolean; // should appeal be sent as a DM?
  active: boolean;
  sent: boolean;
  anonymityLevel: AnonymityLevel;
  /** Indicates the status of the survey delivery to avoid resending messages */
  deliveryState?: DeliveryState;
  description?: string;
  author: string;
  authorName: string;
  sendDate: Date;
  closeDate: Date;
  reminderCount: number;
  reminderFrequency: number;
  reminderFrequencyUnit: DurationUnit;
  command: string;
  deleted: boolean;
  shared: boolean; // allow access on web app to anyone with the link
  /** If true, platform mints a join code for this survey so people can join via a shared code. Follows the web link's auth. */
  joinCodeEnabled?: boolean;
  timestamp?: string;
  updatedAt?: Date;
  appealUpdatedAt?: Date;
  trackId?: string; // FOREIGN KEY: Track
  orgTemplateId?: string; // FOREIGN KEY: OrgTemplate
  globalTemplateId?: string; // FOREIGN KEY: GlobalTemplate
  results: ResultsDeliveryType;
  /**
   * Indicates if the results should be posted when the survey closes.
   */
  postResultsOnClose?: boolean;
  resultsDelivered?: Date;
  appearAs?: string;
  customMetadata?: CustomMetadata;
  /** Foreign key reference to workflows collection */
  workflowId?: string;
  /** Foreign key reference to workflows.action sub object. specifies a single action within a workflow */
  workflowActionId?: string;
  workflowEventId?: string; // FOREIGN KEY: WorkflowEvent
  slackWorkflowConfigurationId?: string; // FOREIGN KEY: SlackWorkflowConfiguration
  /** Integration with google sheets. If set, this Survey should dump live results to this sheet id */
  googleSheetId?: string;
  /** Metadata for google sheet integration */
  googleSheetMetadata?: GoogleSheetMetadata;
  /** Foreign key reference to questionnaire weights collection */
  questionnaireWeightId?: QuestionnaireWeightId;
  /** Only used to identify auto-generated demo surveys */
  scriptGenerationId?: string;

  /** Only used for official pollys - omitting few props as they won't be stored in DB but needed otherwise */
  officialMessageContext?: OfficialMessageContext;
  /** The total number of completed submissions - populated from the platform polly object field of the same name */
  responseCount?: number;
}

export interface IQuiz {
  _id: string;
  createdAt: Date;
  orgId: string; // FOREIGN KEY: Organization
  title: string;
  description?: string;
  outro?: string;
  state: PollyState;
  openAt?: Date;
  closeAt?: Date;
  responseCount?: number;
  authorId: string;
  senderName: string;
  teamId: string[]; // FOREIGN KEY: Team
  sendByDm: boolean;
  anonymity: SurveyAnonymity;
  answersVisibility: QuizAnswersVisibility;
  quizMode: QuizMode;
  questionDuration?: ISO8601Duration;
  livePollyProgressionMode?: LivePollyProgressionMode;
  templateId?: string;
  resultsSettings?: IQuizResultsSettings;
  sharingSettings?: PollySharingSettingsSchema;
  avgScore?: number;
}

export type PlatformQuizResultsSettings = IQuizResultsSettings;

export type QuizResultsSettings = {
  audience?: QuizAudienceSettings;
  presentation?: QuizPresentationSettings;
};

export type QuizAudienceSettings = {
  answersVisibility: QuizAnswersVisibility;
  showIndividualResults: boolean;
  showGroupResults: boolean;
  showLeaderboard: boolean;
  showLeaderboardAfterQuestion: boolean;
};

export type QuizPresentationSettings = {
  answersVisibility: QuizAnswersVisibility;
  showGroupResults: boolean;
  showCorrectAnswer: boolean;
  showLeaderboard: boolean;
  showLeaderboardAfterQuestion: boolean;
};

export const QUIZ_ANSWERS_VISIBILITY = {
  AFTER_QUESTION: "after-question",
  AFTER_COMPLETED: "after-completed",
  AFTER_CLOSE: "after-close",
  DONT_SHARE: "none",
} as const;

export type QuizAnswersVisibility =
  (typeof QUIZ_ANSWERS_VISIBILITY)[keyof typeof QUIZ_ANSWERS_VISIBILITY];

export type JoinCode = {
  enabled?: boolean;
  code?: string;
  routingMetadataId?: RoutingMetadataId;
  state?: JoinCodeState;
};

export interface SurveyInstance {
  _id: string;
  createdAt: Date;
  orgId: string; // FOREIGN KEY: Organization
  channelId: string;
  surveyId: string; // FOREIGN KEY: Survey
  /** Slack ID of the user this was delivered to */
  targetUser: string;
  surveyProgress: number;
  completedPolls: string[];
  startedAt?: Date;
  submittedAt?: Date;
  submitted: boolean;
  deleted: boolean;
  nextRemindDate?: Date;
  isCustomReminder?: boolean;
  isManualReminder?: boolean;
  platform: Platform;
  // whether the user has been shown the first question (as opposed to just the appeal)
  hasSeenFirstQuestion?: boolean;
  // the survey's DM message, can be either the survey appeal or the survey-taking message
  timestamp?: string;
  /** Used for threaded survey results. If this has posted to the thread, this timestamp is a reference to that message */
  threadedReplyTimestamp?: string;
  slackRateLimitingInfo?: RateLimitingInfo;
  /** Indicates the current page to be displayed */
  currentPage?: number;
  /** Cached responses to questions. Used while the person is taking the survey */
  questionResponses?: {
    questionId: string;
    votes: { choiceId: number; weight?: number; rank?: number }[];
    comment?: string;
  }[];
  /** used to allow users to go back and edit / resubmit their survey */
  editing?: boolean;
  skipId?: string; // foreign key Skip._id
  /** foreign keys for results message that were sent. If there are many, all but the last element in the array should be marked deleted */
  resultsMessageIds?: string[];
  /** foreign key of any reminder messages that were sent. If there are many, all but the latest should be marked deleted */
  reminderMessageIds?: string[];
  /** Whether this survey instance is for a platform type polly */
  isPlatformTypeInstance?: boolean;
  /** The type of platform polly this instance is for - only set when isPlatformTypeInstance is true */
  platformPollyType?: "quiz" | "survey";
  /** Completed question indices for after-question quiz mode */
  questionsCompleted?: number[];
  /** Current question index for after-question quiz mode */
  currentQuestionIndex?: number;
  /** Platform response ID for incremental sync in after-question quiz mode */
  platformResponseId?: string;
}

export interface Category {
  _id: string;
  name: string;
  orgId?: string;
  isPrivate?: boolean;
  color: string;
  iconUrl?: string;
}

export interface ITemplateCategory {
  _id: string;
  name: string;
  color: string;
  iconUrl: string;
  key: string;
  title: string;
  showCount: boolean;
  sortOrder?: number;
  parentId?: string;
}

export interface BaseTemplate {
  _id: string;
  createdAt: Date;
  title: string;
  templateTitle?: string;
  description: string;
  outro?: string;
  templatePollyType?: "poll" | "survey" | "quiz";
  categories: TemplateCategoryType[];
  subCategories?: SubTemplateCategory[];
  recurringFrequency: Cadence | "one-time";
  /** Interval multiplier for the recurrence unit (e.g. every 2 weeks). PS-4931 */
  briefInterval?: number;
  /** Selected weekdays (0=Sun..6=Sat) for week-unit recurrences. PS-4931 */
  daysOfWeek?: number[];
  rotating: boolean;
  appeal?: string;
  dmAppeal?: boolean;
  openDuration?: number;
  openDurationUnit?: DurationUnit;
  reminderCount?: number;
  reminderFrequency?: number;
  reminderFrequencyUnit?: DurationUnit;
  anonymityLevel: AnonymityLevel;
  commentsPublic: boolean;
  strictVoting?: boolean;
  results: ResultsDeliveryType;
  appearAs?: string;
  postResultsOnClose: boolean;
  questionnaireWeightId?: QuestionnaireWeightId;
  pollyAudienceIds?: string[];
  isOfficialMessaging?: boolean;
  officialMessagingContext?: OfficialMessageContext;
  isSystemTemplate?: boolean;
  /** Premium gating indicator for templates (applies to system/global templates) */
  isPremiumTemplate?: boolean;
  quizMode?: QuizMode;
  questionDuration?: ISO8601Duration;
  /** Quiz results settings (backend shape). Used when templatePollyType === "quiz". */
  quizResultsSettings?: QuizResultsSettings;
  isQuizAdvancedSettingsEnabled?: boolean;
}

/* Replacement for GlobalTemplates, not stored in db */
export interface StaticTemplate extends BaseTemplate {
  previewImage?: string;
  detailImages: string[];
  categories: TemplateCategoryType[];
  subCategories?: SubTemplateCategory[];
  tagLine: string;
  questions?: StaticTemplateQuestion[];
  modalHeader?: GlobalTemplateModalHeaders;
  isAccessDenied?: boolean;
  isPremiumTemplate?: boolean;
}

export interface StaticTemplateQuestion extends BaseTemplateQuestion {
  queuePosition: number; // Should this be stripped?
}
export type HotTakeGenre = "funny" | "funniest" | "spicy" | "normal";
export interface GeneratedHotTakeResult {
  title: string;
  genre: HotTakeGenre;
}

export type IcebreakerGenre = "funny" | "spicy" | "normal";
export interface GeneratedIcebreakerResult {
  title: string;
  genre: IcebreakerGenre;
}

/* New collection will track just the used counts of global templates */
export interface GlobalTemplateCount {
  _id: string;
  categories: TemplateCategoryType[];
  usedCount: number;
  globalTemplateId: string;
}
export interface TemplateCategory {
  _id: string;
  name: string;
  color: string;
  iconUrl: string;
  key: string;
  title: string;
  showCount: boolean;
  sortOrder?: number;
  parentId?: string;
}
export interface OrgTemplate extends BaseTemplate {
  authorId: string; // slack user id
  authorOrg: string; // FOREIGN KEY: Organization
  teamIds?: string[];
  usedCount: number;
  audience: Audience;
  globalTemplateId?: string; // FOREIGN KEY: GlobalTemplate;
  link: Link;
  deleted: boolean;
  updatedAt?: Date;
  /** Foreign key reference to workflows collection */
  workflowId?: string;
  /** Foreign key reference to workflows.action sub object. specifies a single action within a workflow */
  workflowActionId?: string;
  orgTemplateQuestions?: OrgTemplateQuestion[];
}

export interface BaseTemplateQuestion {
  _id: string;
  createdAt: Date;
  title: string;
  pollType: PollType;
  formatType?: FormatType;
  options: PostOptionsTemplate[];
  commentsPublic: boolean;
  multipleAnswer: boolean;
  allowAddingMcOptions: boolean;
  surveyQuestionRequired: boolean;
  anonymityLevel: AnonymityLevel;
  questionType?: QuestionType; // Will be required once all questions have this
  multilineInput?: boolean; // Optional prop that currently only applies to open-ended polls
  allowComment?: boolean;
  commentPrompt?: string;
  maximumVotes?: number;
  wordcloudEnabled?: boolean;
  /** If true, a respondent may submit more than one response (open ended / word cloud / ice breaker) */
  allowMultipleResponses?: boolean;
}

export interface GlobalTemplateQuestion extends BaseTemplateQuestion {
  globalTemplateId: string; // FOREIGN KEY: GlobalTemplate
  queuePosition: number;
}

export interface OrgTemplateQuestion extends BaseTemplateQuestion {
  queuePosition?: number;
  orgTemplateId: string; // FOREIGN KEY: OrgTemplate
  globalTemplateId?: string; // FOREIGN KEY: GlobalTemplate
  globalTemplateQuestionId?: string; // FOREIGN KEY: GlobalTemplateQuestion
  link: Link;
  deleted: boolean;
}

export interface UnpublishedTemplate {
  _id: string;
  authorId: string;
  orgId: string;
  templateId: string;
  audience: string[];
  slackPollId: string;
  createdAt: Date;
}

export interface PostOptionsTemplate {
  _id: number;
  value: string;
  orderIndex?: number; // Migrate _id to orderIndex and remove
  renderIndex?: number; // What to sort by for the taking/slack results experience
  image_url?: string;
}

export interface QuizPostOptionsTemplate extends PostOptionsTemplate {
  correct: boolean;
}

export interface PostOptions {
  _id: number;
  value: string;
  votes: number;
  emoji: string;
  platformChoiceId?: ChoiceId;
  orderIndex?: number; // Migrate _id to orderIndex
  renderIndex?: number; // What order to show for the taking/slack results experience
  image_url?: string; // For image polls only
}

export interface QuizPostOptions extends PostOptions {
  platformChoiceId: ChoiceId;
  correct: boolean;
}

// AudienceTeamIds is false for limited polls, where we don't use a Team object
// since it's in a slack channel we can't access
export type AudienceTeamIds = string[] | false;

export interface Post {
  _id: string;
  createdAt: Date;
  title: string;
  pollType: PollType;
  org: string; // FOREIGN KEY: Organization
  author: string;
  votes: number;
  options: PostOptions[] | QuizPostOptions[];
  closeDate: Date;
  groupIdFilter: AudienceTeamIds; // FOREIGN KEY: Team
  commentsPublic: boolean;
  questionType?: QuestionType; // Will be required once all questions have this
  formatType?: FormatType;
  trackPollId?: string; // FOREIGN KEY: TrackPoll
  trackId?: string; // FOREIGN KEY: Track
  briefGroupId?: string; // FOREIGN KEY: BriefGroup
  /** @deprecated use anonymityLevel on the parent object */
  anonymityLevel: AnonymityLevel;
  multipleAnswer?: boolean;
  surveyQuestionRequired: boolean; // Only post inside of surveys may be required
  allowAddingMcOptions: boolean;
  currentMapping: string;
  commentCount?: number;
  deleted: boolean;
  isLimited?: boolean;
  surveyId?: string; // FOREIGN KEY: Survey
  allowComment?: boolean;
  commentPrompt?: string; // A prompt to show users when letting them comment.
  queuePosition?: number;
  orgTemplateQuestionId?: string; // FOREIGN KEY: OrgTemplateQuestion
  maximumVotes?: number;
  multilineInput?: boolean; // Optional prop that currently only applies to open-ended polls
  // PS-5438: how many answers one participant may give to this question. Open-ended survey
  // questions only; absent or 1 everywhere else.
  maxResponses?: number;
  /**
   * The longest answer the platform will accept for an open-ended question. Absent means
   * uncapped, which is the platform default and every Slack-authored question.
   */
  contentMaxLength?: number;
  mappingCache?: Partial<Mapping>[];
  customMetadata?: CustomMetadata;
  updatedAt?: Date;
  /** Foreign key reference to workflows collection */
  workflowId?: string;
  /** Foreign key reference to workflows.action sub object. specifies a single action within a workflow */
  workflowActionId?: string;
  workflowEventId?: string; // FOREIGN KEY: WorkflowEvent
  scriptGenerationId?: string; // used when generating developer demo data via the UI
  wordcloudEnabled?: boolean;
  /** If true, a respondent may submit more than one response (open ended / word cloud / ice breaker) */
  allowMultipleResponses?: boolean;
}

export const UNPUBLISHED_POLL_TIME_FORMAT = "h:mm A";
export const UNPUBLISHED_POLL_DAY_FORMAT = "ddd MMM D, YYYY";

export interface Mapping {
  _id: string;
  createdAt: Date;
  platform: Platform; // FOREIGN KEY: Organization
  user: string; // PLATFORM KEY: Slack/Teams User ID
  post: string; // FOREIGN KEY: Post
  org: string; // FOREIGN KEY: Organization
  mappedName: string;
  vote: number; // FOREIGN KEY: Post.options._id
  weight?: number;
  rank?: number;
  deleted?: boolean;
  scriptGenerationId?: string;
}

export interface Comment {
  _id?: string;
  createdAt: Date;
  post: string; // FOREIGN KEY: Post
  text: string;
  user: string; // FOREIGN KEY: Mapping
  org: string; // FOREIGN KEY: Organization
  replyTo: string;
  userId?: string; // Slack User ID
  userName: string;
  // PS-5210: platform ResponsePlatformMetadata._id (the platform response id) that this comment
  // maps to. Set once when the comment is first forwarded to the platform so that editing an
  // individual response on a multiple-response polly updates the correct platform response
  // instead of creating a duplicate.
  platformResponseId?: string;
  // PS-5438: the platform `QuestionResponse` entry this comment is. One respondent may hold
  // several answers to one open-ended question, and each is its own entry — so an edit or a
  // withdrawal has to name the entry, not just the response.
  platformQuestionResponseId?: string;
}

export interface Skip {
  _id: string;
  createdAt: Date;
  updatedAt?: Date;
  trackId?: string; // FOREIGN KEY: Track._id
  userId: string; // FOREIGN KEY: User._id
  orgId: string; // FOREIGN KEY: Organization._id
  fromDate?: Date;
  toDate?: Date;
  reason?: string;
  active: boolean;
  timestamp: string;
  deleted: boolean;
}

export interface Team {
  _id: string;
  createdAt: Date;
  platform: Platform;
  orgId: string; // FOREIGN KEY: Organization
  slackTeamIdsInThisChannel?: string[]; // Slack ids of organizations this channel is shared with
  sharedSlackTeamIdsLastUpdated?: Date; // Last time conversations.info was called to update slackTeamIdsInThisChannel
  title: string; // PLATFORM KEY: Channel name in Slack/Teams etc.
  lead?: string[]; // PLATFORM KEY: Platform User (used for Slack DM channels)
  slackId?: string; // PLATFORM KEY: Channel ID in Slack/Teams etc.
  deleted: boolean;
  hangoutsToken?: string; // PLATFORM KEY: Hangouts per-channel API token, currently not used

  // PLATFORM KEY: Whether this is a private channel or a MPIM
  // This is important in terms of how they're displayed in Slack
  isGroup?: boolean;

  // PLATFORM KEY: Whether the bot is a member of the channel or not
  // This is important for shared channels where polls without membership can cause
  // poor behavior for foreign users
  isMember?: boolean;

  // PLATFORM KEY: Whether this channel is the Slack general channel
  isGeneral?: boolean;

  // PLATFORM KEY: Whether or not this channels is private
  isPrivate?: boolean;

  // PLATFORM KEY: Whether or not this channel is shared
  isShared?: boolean;

  // PLATFORM KEY: If channel is shared externally or pending it
  isExtShared?: boolean;

  // PLATFORM KEY: If channel is shared on Enterprise Grid
  isEGShared?: boolean;

  // PLATFORM KEY: Whether this channel is marked as read-only in Slack
  isReadOnly?: boolean;

  // Slack only (currently): Number of members, including bots but excluding deleted users.
  // For public channels, we use the num_members property, for private groups we check the members
  // list and filter out deleted or foreign users to get the count (so it underestimates the real
  // number by excluding foreign users).
  // For DM channels, we don't set it.
  // Also note that archived teams have numMembers: 0.
  numMembers?: number;
  members?: string[]; // list members in a channel, including bots

  userCountByType?: {
    primary_owner: number;
    enterprise_owner: number;
    enterprise_admin: number;
    owner: number;
    admin: number;
    ultra_restricted: number;
    restricted: number;
    member: number;
  };
  membersUpdatedAt?: Date; // last updated timestamp for members pulled from slack api
}

export interface SlackUserGroup {
  _id: string;
  orgId: string;
  teamId: string;
  groupId: string;
  name: string;
  handle: string;
  description?: string;
  isExternal: boolean;
  isDeleted: boolean;
  userCount: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  lastSyncedAt: Date;
}

export enum SyncType {
  SlackUserGroups = "slack_user_groups",
  SlackUserGroupMembers = "slack_user_group_members",
}

export enum SyncStatus {
  Pending = "pending",
  InProgress = "in_progress",
  Completed = "completed",
  PartiallyCompleted = "partially_completed",
  Failed = "failed",
}

export enum SyncTrigger {
  AdminEnabled = "admin_enabled",
  SlackEvent = "slack_event",
  ManualSync = "manual_sync",
  ScheduledSync = "scheduled_sync",
}

export interface SyncHistoryMetadata {
  groupsProcessed?: number;
  groupsCreated?: number;
  groupsUpdated?: number;
  groupsFailed?: number;
  membersProcessed?: number;
  membersAdded?: number;
  membersRemoved?: number;
  membersFailed?: number;
  slackEventType?: string;
  groupId?: string;
  groupName?: string;
  failedGroups?: string[];
  failedMembers?: string[];
  errors?: string[];
}

export interface SyncHistory {
  _id: string;
  type: SyncType;
  orgId: string;
  enterpriseId?: string;
  teamId?: string;
  trigger: SyncTrigger;
  status: SyncStatus;
  error?: string;
  metadata?: SyncHistoryMetadata;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
}

export interface Track {
  _id: string;
  createdAt: Date; // Added late, doesn't exist on all tracks
  orgId: string; // FOREIGN KEY: Organization
  teamId: string[];
  title: string;
  active: boolean;
  description: string;
  rotating: boolean;
  briefCadence: Cadence;
  /**
   * Interval multiplier for the cadence unit. Defaults to 1 when absent.
   * e.g. briefCadence=WEEKLY + briefInterval=2 means "every 2 weeks". Added for PS-4931.
   */
  briefInterval?: number;
  /**
   * Selected weekdays (0=Sun..6=Sat) for week-unit recurrences. Only meaningful when
   * briefCadence is a week unit (WEEKLY/BIWEEKLY/TRIWEEKLY). When absent/empty the track
   * sends once per cycle on nextBriefDate's weekday (legacy behavior). Added for PS-4931.
   */
  daysOfWeek?: number[];
  mqAllowWeekends: boolean; // mq stands for monthly/quarterly
  originalNextBriefDate: Date; // Added late, doesn't exist on all tracks
  nextBriefDate?: Date;
  openDuration?: number;
  openDurationUnit?: DurationUnit;
  author?: string; // Doesn't exist on email-based org tracks
  authorName?: string; // Doesn't exist on email-based org tracks
  timezone: string; // Added late, doesn't exist on all inactive tracks (exists on active ones)
  tzMigrationDone: boolean; // DEPRECATED: Used for a one-time migration to support timezones
  strictVoting?: boolean;
  anonymityLevel: AnonymityLevel;
  commentsPublic: boolean; // Added late, doesn't exist on all inactive tracks (exists active ones)
  command: string; // Added late, doesn't exist on all inactive tracks (exists on active ones)
  deleted: boolean;
  shared: boolean; // allow access on web app to anyone with the link
  appeal?: string;
  dmAppeal?: boolean; // surveys only
  reminderCount?: number;
  reminderFrequency?: number;
  reminderFrequencyUnit?: DurationUnit;
  orgTemplateId?: string; // FOREIGN KEY: OrgTemplate
  globalTemplateId?: string; // FOREIGN KEY: GlobalTemplate

  // Some cached data, to reduce the need for lookups and aggregations in other collections
  lastSentInfo?: LastSentInfo; // date(s) and id(s) of when it was last sent out
  numTrackPolls: number; // number of (active) trackpolls it has
  results: ResultsDeliveryType;
  /** If true, should post a message with results when the poll / survey closes */
  postResultsOnClose?: boolean;
  appearAs?: string;
  authorDisableNotificationMessageId?: string; // FOREIGN KEY: SlackMessage
  /** Integration with google sheets. If set, this track should dump live results to this sheet id */
  googleSheetId?: string;
  /** Metadata for google sheet integration */
  googleSheetMetadata?: GoogleSheetMetadata;
  /** Foreign key reference to questionnaire weights collection */
  questionnaireWeightId?: QuestionnaireWeightId;

  /** Only used for official pollys - omitting few props as they won't be stored in DB but needed otherwise */
  officialMessageContext?: OfficialMessageContext;

  platformSeriesId?: SeriesId;

  /**
   * PS-5916: rotate a recurring Hot Take / Icebreaker through its template library.
   * After each send the track's only trackpoll gets the next unused library question.
   * Absent or null means the same question every time.
   */
  questionRotation?: TrackQuestionRotation | null;
}

/** PS-5916: the template libraries a recurring polly can rotate through */
export type QuestionRotationLibrary =
  | FormatType.hotTake
  | FormatType.icebreaker;

export interface TrackQuestionRotation {
  library: QuestionRotationLibrary;
  /** Library question ids sent or queued in the current cycle. Cleared once every question has been used */
  usedQuestionIds: string[];
}

export interface PollLastSentInfo {
  date: Date;
  postId: string;
  trackPollId: string;
}

export interface SurveyLastSentInfo {
  date: Date;
  surveyId: string;
}

export type LastSentInfo =
  | PollLastSentInfo // single poll
  | PollLastSentInfo[] // rotating track
  | SurveyLastSentInfo;

export interface TrackPoll {
  _id: string;
  createdAt: Date; // Added late, doesn't exist on all trackpolls
  org: string; // FOREIGN KEY: Organization (Added late, doesn't exist on some orphaned trackpolls)
  trackId: string; // FOREIGN KEY: Track
  title: string;
  appeal?: string;
  pollType: PollType;
  formatType?: FormatType; // Format type for special poll formats (e.g., icebreaker)
  options: PostOptions[];
  queuePosition: number;
  commentsPublic: boolean; // Added late, doesn't exist on all
  active: boolean;
  author: string; // Doesn't exist on orphaned trackpolls or email-based tracks
  authorName: string; // Doesn't exist on orphaned trackpolls or email-based tracks
  multipleAnswer: boolean; // Added late, doesn't exist on all
  allowAddingMcOptions: boolean; // Added late, doesn't exist on all
  surveyQuestionRequired: boolean; // Added late, doesn't exist no all
  anonymityLevel: AnonymityLevel;
  questionType?: QuestionType; // Will be required once all questions have this
  multilineInput?: boolean; // Optional prop that currently only applies to open-ended polls
  allowComment?: boolean;
  commentPrompt?: string; // surveys only
  deleted: boolean;
  orgTemplateQuestionId?: string; // FOREIGN KEY: OrgTemplateQuestion
  globalTemplateQuestionId?: string; // FOREIGN KEY: GlobalTemplateQuestion
  maximumVotes?: number;
  wordcloudEnabled?: boolean;
  /** If true, a respondent may submit more than one response (open ended / word cloud / ice breaker) */
  allowMultipleResponses?: boolean;
}

export interface DMChat {
  _id: string;
  createdAt: Date;
  orgId: string; // FOREIGN KEY: Organization
  userId: string;
  teamId: string;
  channelId: string;
  userName?: string;
  isInitialUser?: boolean;
  completedIntro?: boolean;
  grandfathered?: boolean;
  hadPersonalInteraction?: boolean;
  hasReceivedDMPolls?: boolean;
  lastInteraction?: Date;
  hasVotedOnGeneralPolls?: boolean;
  hasVotedOnDMPolls?: boolean;
  legacyBotToken?: boolean;
  quietUntil?: Date;
  doNotRespond?: boolean; // Prevent responses to get a handle on NLP API rate limits (rogue bots)
  silenceMarketingInfo?: boolean;
}

export enum ExportStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
}

export enum ExportType {
  CSV = "CSV",
}

export interface ExportData {
  type: ExportType;
  content: string;
}

export interface CsvExportData extends ExportData {
  type: ExportType.CSV;
  csvLines: number;
}

export type SavedExport = CompletedExport | PendingExport;

export interface BaseExport {
  _id: string;
  exportDate: Date;
  status: ExportStatus;
  exportAuthorId: string; // slack user id
  exportAuthorName?: string;
  orgId: string;
  dmUser: true;
  deleted?: boolean;
}

export interface CompletedExport extends BaseExport {
  status: ExportStatus.ACTIVE;
  data: ExportData;
  exportMetadata: ExportMetadata;
  lastDownloaded?: Date;
}

export interface PendingExport extends BaseExport {
  status: ExportStatus.PENDING;
}

export type ExportMetadata =
  | BulkExportMetadata
  | SingleExportMetadata
  | TrackExportMetadata;

export interface BulkExportMetadata {
  exportType: "bulk";
  startDate?: string;
  endDate?: string;
  title?: string;
}

export interface SingleExportMetadata {
  exportType: "poll" | "survey" | "quiz";
  id: string;
  title: string;
  createDate?: Date;
  authorName: string;
  channelName: string[];
  recurring: boolean;
}

export interface TrackExportMetadata {
  exportType: "recurring";
  trackId: string;
  title: string;
  authorName: string;
  channelName: string[];
  cadence: Cadence;
  createDate?: Date;
  startDate?: string;
  endDate?: string;
}

// The minimum set of data needed to create a Post/TrackPoll.
// Does not include any instance-relevant information like org, user, channel, etc.
// TODO could make this the base export interface for TrackPoll,
// would need to migrate `optionArray` and change `trackPollId` to `_id`.
export interface PostTemplate {
  title: string;
  appeal?: string;
  pollType: PollType;
  questionType?: QuestionType;
  formatType?: FormatType;
  optionArray?: PostOptionsTemplate[];
  multipleAnswer: boolean;
  allowAddingMcOptions: boolean;
  commentsPublic?: boolean;
  anonymityLevel: AnonymityLevel;
  multilineInput?: boolean; // Optional prop that currently only applies to open-ended polls
  trackPollId?: string; // needed to create posts in recurring case
  queuePosition?: number; // only for surveys
  options?: PostOptionsTemplate[]; // alternative to optionArray, used in surveys
  allowComment?: boolean;
  surveyQuestionRequired: boolean; // only for surveys
  commentPrompt?: string;
  orgTemplateQuestionId?: string;
  isLimited?: boolean;
  maximumVotes?: number;
  limitedTargetId?: string;
  wordcloudEnabled?: boolean;
  /** If true, a respondent may submit more than one response (open ended / word cloud / ice breaker) */
  allowMultipleResponses?: boolean;
}

export function isBrief(b: Brief | BriefGroup): b is Brief {
  return (b as any).briefGroupId;
}

// This type is an extension of the Meteor.User type
export interface User {
  _id: string;
  createdAt: Date;
  profile: UserProfile;
  services: UserServices;
  api?: UserApiInfo;
  hasActiveSubscription?: boolean; // for use in admin app only
}

export interface InternalApiUser {
  _id: string;
  name: string;
  type: "service";
  scopes: [];
  createdAt: Date;
  updatedAt: Date;
  secrets: {
    name: string;
    value: string;
    createdAt: Date;
  }[];
}

export interface UserApiInfo {
  token?: {
    value: string;
    createdAt: Date;
    expires?: Date;
  };
  callback?: string;
}

export type ItemType =
  | "briefgroup"
  | "survey"
  | "workflow"
  | "template"
  | "track"
  | "surveyTrack"
  | "qna"
  | "quiz";
export type ProfileSavedItems = {
  [tagType in ItemType]?: Array<string>;
};

export type ProfileSavedTags = {
  [tagName: string]: {
    items: ProfileSavedItems;
    color: string;
  };
};

export interface UserSavedItems {
  favorites: ProfileSavedItems;
  tags: ProfileSavedTags;
}

// Onboarding status to show interstitial modal
// ctaType -> status (true: onboarded, false: first time)
export interface UserOnboardingStatus {
  q_and_a: boolean;
  word_clouds: boolean;
  surveys: boolean;
  quiz: boolean;
  trivia: boolean;
  team_trivia: boolean;
  hot_takes: boolean;
  standup: boolean;
  pulse_survey: boolean;
  icebreaker: boolean;
}

export enum CtaType {
  MULTIPLE_CHOICE = "multiple_choice",
  OPEN_ENDED = "open_ended",
  RATING = "rating",
  QNA = "q_and_a",
  WORD_CLOUDS = "word_clouds",
  SURVEYS = "surveys",
  QUIZ = "quiz",
  TRIVIA = "trivia",
  TEAM_TRIVIA = "team_trivia",
  HOT_TAKES = "hot_takes",
  STANDUP = "standup",
  PULSE_SURVEY = "pulse_survey",
  ICEBREAKER = "icebreaker",
  RANK_ORDER = "rank_order",
  POINT_ALLOCATION = "point_allocation",
  EMOJI_RANK = "emoji_rank",
  NPS = "nps",
  YES_NO = "yes_no",
  NOMINAL_IMAGE = "nominal_image",
  PRESENTATION = "presentation",
  PAIRING = "pairing",
}

export interface UserProfile {
  client: Platform;
  firstName: string;
  lastName: string;
  notify: number;
  org: string; // FOREIGN KEY: Organization
  orgAdmin: boolean;
  orgName: string;
  setupComplete: boolean;
  teamTags: string[];
  tree: string;
  onboardingStatus?: UserOnboardingStatus;
  optIns?: OptIns;
  name?: string;
  real_name?: string;
  display_name?: string;
  is_bot?: boolean; // slack boolean for identifying bot users
  deleted?: boolean; // slack boolean meaning the user is no longer in this team
  deleted_date?: Date;
  slackAvatarUrls?: SlackAvatarUrls;
  slackRole?: SlackRole;
  tz?: string; // timezone
  email?: string;
  user_id?: string;
  team?: string;
  team_id?: string;
  url?: string;
  needsActivation?: boolean;
  stripe?: string;
  secondaryProfile?: UserProfile;
  silenceMarketingInfo?: boolean;
  slackDirectChannel?: string; // Slack ID of the users DM channel
  savedItems?: UserSavedItems;
  title?: string; // Slack profile.title (undocumented)
  needsOnboarding?: boolean;
  needsMarketingOptIn?: boolean;
  productInterests?: Array<ProductInterests>;
  quietUntil?: Date;
  hubspotUtk?: string;
  hubspotContactId?: string;
  doNotRespond?: boolean;
  doNotRespondDM?: boolean; // Prevent responses to the dm to polly bot , it is to avoid bots who DM contineously to polly bot
  nudgeMessages?: Array<NudgeMessage>;
  responseWarningMessages?: Array<ResponseWarningMessage>; // list of slackMessageIds
  recievingTrialNudgeMessages?: boolean;
  authorEphemeralMessagesSent?: number;
  hasTakenPollyTour?: boolean;
  trialRestartEligibility?: boolean;
  redeemedCyberMondayPromo2021?: boolean;
  npsDmAppeal?: {
    qualified: boolean; // whether the user meets the condition to receive nps
    lastSent?: Date; // when the last nps appeal DM was sent
  };
  demoCTAEnabled?: boolean;
  cohorts?: {
    checkoutConfirmation?: boolean;
  };
  marketingMessages?: DMCampaign[];
  receivingMarketingMessages?: boolean;
  marketingDMProcessingStartedAt?: Date;
  unfurlScopePromptSentAt?: Date;
  unfurlScopePromptDismissedAt?: Date;
  // Self-serve onboarding persona data
  persona?: string;
  slideSoftware?: string;
  slackGroups?: string[];
}

// This is a subset of the slack user object, with just the parts about roles
export interface SlackRolesInfo {
  is_admin?: boolean;
  is_owner?: boolean;
  is_primary_owner?: boolean;
  is_restricted?: boolean;
  is_ultra_restricted?: boolean;
  enterprise_user?: {
    is_admin: boolean;
    is_owner: boolean;
  };
}

export enum SlackRole {
  // Ordered enum of roles in slack.
  // See https://get.slack.help/hc/en-us/articles/201314026-Roles-and-permissions-in-Slack for single workspaces,
  // and https://get.slack.help/hc/en-us/articles/115001382787-Roles-and-permissions-on-an-organization for EG.
  // Use steps of 5 in case we want to squeeze more values into the gaps (doubtful)
  SINGLE_CHANNEL_GUEST = 5,
  GUEST = 10,
  MEMBER = 15,
  ADMIN = 20,
  OWNER = 25,
  // These two roles are at the enterprise grid level.
  // EG_ADMIN is strictly higher than OWNER, because it in fact has all the abilities that a single-workspace owner has.
  // See https://get.slack.help/hc/en-us/articles/115001382787-Roles-and-permissions-on-an-organization .
  // The only exception is an EG_ADMIN who is not in the workspace in question, but that's not a problem for us,
  // because then we either won't have them as a user of that workspace or they'd have deleted: true (if they were
  // removed), so it's irrelevant for us.
  EG_ADMIN = 30,
  EG_OWNER = 35,
  // Primary owner is the highest role there is, both for EG and for non-EG.
  // The EG primary owner is also the primary owner for all of that EG's workspaces,
  // so we don't distinguish between EG and non-EG primary ownership.
  PRIMARY_OWNER = 40,
}

export enum ProductInterests {
  HR = "HR",
  AGILE = "AGILE",
  MANAGER = "MANAGER",
  OTHER = "OTHER",
  // everything below is deprecated but may still be in users profiles
  DEV = "DEV",
  PM = "PM",
  LEADER = "LEADER",
  IT = "IT",
  SOCIAL = "SOCIAL",
}

/** Used to send a drip campaign of messages to a user during their trial */
export interface NudgeMessage {
  /** unique name of the message, e.g. initialMessage */
  messageName: NudgeMessageType;
  /** when the message should send. */
  sendDate: Date;
  /** when the card is expiring */
  expiringDate?: Date;
  /** mongo id of the associated SlackMessage document. null if not sent yet */
  messageId?: string;
  isValid?: boolean;
  isDeleted?: boolean;
  renewalDate?: Date;
  /** metadata for storing additional context about the message */
  metadata?: {
    subscriptionId?: string; // subscription ID for retention messages
    abandonedPlanName?: string; // name of the abandoned plan for abandoned cart messages
    pollyId?: string; // polly the orphaned-polly nudge references (PS-5517)
    pollyType?: string; // "poll" | "survey" for the orphaned-polly nudge
    pollyTitle?: string; // title shown in the orphaned-polly nudge copy
    pollyCreatedAt?: Date; // creation time, drives "created N days ago" copy
    channelId?: string; // pairings channel for the near-limit DM (PS-5516)
    configId?: string; // pairing config for the near-limit DM (PS-5516)
  };
}

export enum NudgeMessageType {
  THREE_DAYS_AFTER_TRAIL = "threeDaysAfterTrail", // deprecated (phasing out)
  SEVEN_DAYS_AFTER_TRAIL = "sevenDaysAfterTrail", // deprecated (phasing out)
  SEVEN_DAYS_AFTER_TRAIL_V2 = "sevenDaysAfterTrailV2",
  SEVEN_DAYS_AFTER_TRAIL_ENDS = "sevenDaysAfterTrailEnds", // deprecated (phasing out)
  SEVEN_DAYS_AFTER_TRAIL_ENDS_V2 = "sevenDaysAfterTrailEndsV2",
  SEVEN_DAYS_AFTER_TRAIL_RESTART = "sevenDaysAfterTrailRestart",
  THIRTY_FIVE_DAYS_AFTER_TRAIL_RESTART = "thirtyFiveDaysAfterTrailRestart",
  SIXTY_THREE_DAYS_AFTER_TRAIL_RESTART = "sixtyThreeDaysAfterTrailRestart",
  NINETY_ONE_DAYS_AFTER_TRAIL_RESTART = "ninetyOneDaysAfterTrailRestart",
  FOURTEEN_DAYS_AFTER_TRAIL_ENDS = "fourteenDaysAfterTrailEnds",
  SIX_DAYS_AFTER_TRIAL_START_GSHEET = "sixDaysAfterTrialStartGSheet",

  // Trial Lifecycle DM Campaign (Nov 2025)
  TRIAL_DM_NOV2025_1 = "trialDmNov2025_1", // n+10 mins - Templates
  // PS-5215 (Jul 2026): Pairings DM inserted at n+3, new enrollments only
  TRIAL_DM_PAIRINGS = "trialDmPairings", // n+3 days - Pairings
  TRIAL_DM_NOV2025_2 = "trialDmNov2025_2", // n+7 days - Trial ending
  TRIAL_DM_NOV2025_3 = "trialDmNov2025_3", // n+14 days - Presentations
  TRIAL_DM_NOV2025_4 = "trialDmNov2025_4", // n+30 days - Social Games
  TRIAL_DM_NOV2025_5 = "trialDmNov2025_5", // n+60 days - Quizzes
  TRIAL_DM_NOV2025_6 = "trialDmNov2025_6", // n+90 days - Themes
  TRIAL_DM_NOV2025_7 = "trialDmNov2025_7", // n+120 days - Word Clouds
  TRIAL_DM_NOV2025_8 = "trialDmNov2025_8", // n+150 days - Q&A

  // Trial Lifecycle DM Campaign (Sep 2025)
  DISABLE_TRIAL_TRACK_DM1 = "disableTrialTrackDM1",
  DISABLE_TRIAL_TRACK_DM2 = "disableTrialTrackDM2",
  DISABLE_TRIAL_TRACK_DM3 = "disableTrialTrackDM3",

  SEVEN_DAYS_AFTER_ONE_TIME_SUB_PURCHASE = "sevenDaysAfterOneTimeSubPurchase",
  CARD_EXPIRING = "cardExpiring",

  // Retention DM campaign types
  RETENTION_DM_60_DAYS = "retentionDm60Days",
  RETENTION_DM_30_DAYS = "retentionDm30Days",
  RETENTION_DM_14_DAYS = "retentionDm14Days",
  RETENTION_DM_7_DAYS = "retentionDm7Days",
  RETENTION_DM_1_DAY = "retentionDm1Day",

  // Win Back DM campaign types
  WIN_BACK_DM_30_DAYS = "winBackDm30Days", // n+30 - Features
  WIN_BACK_DM_60_DAYS = "winBackDm60Days", // n+60 - Templates
  WIN_BACK_DM_90_DAYS = "winBackDm90Days", // n+90 - Presentations
  WIN_BACK_DM_120_DAYS = "winBackDm120Days", // n+120 - Social games

  // Abandoned cart DM
  ABANDONED_CART_DM_1 = "abandonedcart_dm1", // n + 2 hours - Upgrade
  ABANDONED_CART_DM_2 = "abandonedcart_dm2", // n + 72 hours - Templates

  // Self-serve Onboarding DM Campaign (Nov 2025)
  ONBOARDING_DM_1 = "onboardingDm1", // n+10 mins - Use case intake
  ONBOARDING_DM_2 = "onboardingDm2", // n+7 days - Overview + Self Help
  ONBOARDING_DM_3 = "onboardingDm3", // n+15 days - Web Dashboard (n+30 for pre-Jul-2026 enrollments)
  // PS-5215 (Jul 2026): Pairings DM inserted at n+30, new enrollments only
  ONBOARDING_DM_PAIRINGS = "onboardingDmPairings", // n+30 days - Pairings
  ONBOARDING_DM_4 = "onboardingDm4", // n+60 days - Templates
  ONBOARDING_DM_5 = "onboardingDm5", // n+90 days - Themes
  ONBOARDING_DM_6 = "onboardingDm6", // n+120 days - Presentations
  ONBOARDING_DM_7 = "onboardingDm7", // n+150 days - Quiz
  ONBOARDING_DM_8 = "onboardingDm8", // n+180 days - Google Sheets
  ONBOARDING_DM_9 = "onboardingDm9", // n+210 days - Social Games
  ONBOARDING_DM_10 = "onboardingDm10", // n+240 days - Word Clouds
  ONBOARDING_DM_11 = "onboardingDm11", // n+270 days - Q&A
  ONBOARDING_DM_12 = "onboardingDm12", // n+299 days - Tips & Tricks

  // New Org DM Campaign (Nov 2025) - Free plan org installers
  NEW_ORG_DM_1 = "newOrgDm1", // n+7 - Overview + Self Help
  // PS-5215 (Jul 2026): Pairings DM inserted at n+11, new enrollments only
  NEW_ORG_DM_PAIRINGS = "newOrgDmPairings", // n+11 - Pairings
  NEW_ORG_DM_2 = "newOrgDm2", // n+14 - Quiz
  NEW_ORG_DM_3 = "newOrgDm3", // n+21 - Social Games
  NEW_ORG_DM_4 = "newOrgDm4", // n+28 - Templates
  NEW_ORG_DM_5 = "newOrgDm5", // n+35 - Themes
  NEW_ORG_DM_6 = "newOrgDm6", // n+42 - Presentations
  NEW_ORG_DM_7 = "newOrgDm7", // n+49 - Word Clouds
  NEW_ORG_DM_8 = "newOrgDm8", // n+56 - Q&A

  // Celebration DM - first polly with 2+ responses
  CELEBRATION_FIRST_CREATION = "celebrationFirstCreation",

  // Celebration DM - first response to any polly type
  CELEBRATION_FIRST_RESPONSE = "celebrationFirstResponse",

  // Team Plan Upsell DM Campaign (Feb 2026) - PRO to TEAM for small/medium orgs
  TEAM_PLAN_UPSELL_DM_1 = "teamPlanUpsellDm1", // n+35 days
  TEAM_PLAN_UPSELL_DM_2 = "teamPlanUpsellDm2", // n+95 days
  TEAM_PLAN_UPSELL_DM_3 = "teamPlanUpsellDm3", // n+155 days

  // Basic-to-PRO Upsell DM Campaign (Apr 2026) - BASIC renewal -> 3-month free PRO promo
  BASIC_PRO_UPSELL_PLAN_SWITCH = "basicProUpsellPlanSwitch", // n+1 - silent DB switch BASIC->PRO
  BASIC_PRO_UPSELL_DM_1 = "basicProUpsellDm1", // n+2 - Welcome DM
  BASIC_PRO_UPSELL_DM_2 = "basicProUpsellDm2", // n+92 - End-of-promo DM
  BASIC_PRO_UPSELL_REVERT = "basicProUpsellRevert", // n+93 - silent DB revert PRO->BASIC
  // Unassigned License DM Campaign (Apr 2026) - PRO billing admins with unused seats
  UNASSIGNED_LICENSE_DM = "unassignedLicenseDm",

  // Orphaned Polly Nudge (Aug 2026, PS-5517) - channel-less polly created but
  // never sent/shared. DM1 sends via a delayed SQS message (~5 min, precise);
  // DM2 rides the hourly sweep (n+2 days, weekend-deferred).
  ORPHANED_POLLY_DM_1 = "orphanedPollyDm1",
  ORPHANED_POLLY_DM_2 = "orphanedPollyDm2",
  // Pairings near-limit nudge (Aug 2026, PS-5516) — sent inline from the
  // pairing pool-growth triggers when a free-plan pairings channel reaches
  // 20-24 humans; the hourly sweep never sends it. Once ever per user.
  PAIRINGS_FUTURELIMIT_DM_1 = "pairingsFuturelimitDm1",
  // Pairings hit-limit nudge (Aug 2026, PS-5516) — sent inline when the pool
  // crosses the free cap (≥25 humans) and matches pause. Covers bulk invites
  // that jump the 20-24 warning band. Once ever per user; sweep never sends.
  PAIRINGS_LIMIT_DM_1 = "pairingsLimitDm1",
}

/** Used to track warning messages sent when a scheduled polly won't send because the user is over their response limit.  A maximum of one message should send each month. */
export interface ResponseWarningMessage {
  /** when the message sent */
  sendDate: Date;
  /** mongo id of the associated SlackMessage document. */
  messageId?: string;
}

export interface OptIns {
  basicConsent: boolean;
  marketing?: boolean;
  analytics?: boolean;
  grandfathered?: boolean;
  webappIAV1Enabled?: boolean;
}

export interface SlackAvatarUrls {
  image_24: string;
  image_32: string;
  image_48: string;
  image_72: string;
  image_192: string;
  image_512: string;
}

export interface UserServices {
  slack?: {
    id: string;
    accessToken?: string;
    scope?: string[];
    bot?: {
      bot_user_id: string;
      bot_access_token: string;
    };
  };
  google?: {
    access_token?: string;
    scope?: string;
    token_type?: string;
    id_token?: string;
    expiry_date?: number;
    refresh_token?: string;
  };
  resume?: {
    loginTokens?: LoginToken[];
  };
  platform?: UserPlatformMetadata;
}

// Polly Platform user metadata
export interface UserPlatformMetadata {
  id: PollyUserId;
}

// Used in the Admin app to impersonate users
interface LoginToken {
  when?: Date;
  hashedToken?: string;
  debug?: boolean;
  createdBy?: string;
}

/**
 * Parameters for a relational database report.
 * Includes things like data range, columns, id.
 * See unioned types for specific reports.
 */
export type ReportParams =
  | RawVotesByPollParams
  | RawVotesBySurveyParams
  | RawVotesByOrganizationParams
  | RawVotesForRecurringSurveyParams
  | RawVotesForRecurringPollParams
  | RawVotesForLatestRecurringSurveyParams
  | RawVotesForLatestRecurringPollParams
  | UserByUserSurveyParams
  | UserByUserLatestSurveyInTrackParams
  | UserByUserSurveyInTrackParams
  | UserByUserSurveyInTrackByStartedAtParams
  | QuizReportParams;

/**
 * Params to run rawVotesByPoll report
 */
export type RawVotesByPollParams = {
  name: "rawVotesByPoll";
  pollId: string;
  startTime?: string;
  endTime?: string;
  hideWeights?: boolean;
};

/**
 * Params to run rawVotesBySurvey report
 */
export type RawVotesBySurveyParams = {
  name: "rawVotesBySurvey";
  surveyId: string;
  hideWeights?: boolean;
};

/**
 * Params to run rawVotesByOrganization report
 */
export type RawVotesByOrganizationParams = {
  name: "rawVotesByOrganization";
  author: string;
  startTime?: string;
  endTime?: string;
  hideWeights?: boolean;
};

export type RawVotesForRecurringSurveyParams = {
  name: "rawVotesForRecurringSurvey";
  trackId: string;
  hideWeights?: boolean;
};
export type RawVotesForRecurringPollParams = {
  name: "rawVotesForRecurringPoll";
  trackId: string;
  startTime?: string;
  endTime?: string;
  hideWeights?: boolean;
};
export type RawVotesForLatestRecurringSurveyParams = {
  name: "rawVotesForLatestRecurringSurvey";
  trackId: string;
  hideWeights?: boolean;
};
export type RawVotesForLatestRecurringPollParams = {
  name: "rawVotesForLatestRecurringPoll";
  pollId: string;
  hideWeights?: boolean;
};
export type UserByUserSurveyParams = {
  name: "userByUserSurvey";
  surveyId: string;
  hideWeights?: boolean;
};
export type UserByUserLatestSurveyInTrackParams = {
  name: "userByUserLatestSurveyInTrack";
  trackId: string;
  hideWeights?: boolean;
};
export type UserByUserSurveyInTrackParams = {
  name: "userByUserSurveyInTrack";
  trackId: string;
  before?: Date;
  after?: Date;
  hideWeights?: boolean;
};
export type UserByUserSurveyInTrackByStartedAtParams = {
  name: "userByUserSurveyInTrackByStartedAt";
  trackId: string;
  before?: Date;
  after: Date;
  hideWeights?: boolean;
};

export type QuizReportParams = {
  name: "quizReport";
  quizId: string;
  userId?: string;
};

/**
 * Specifies where and how a report will be delivered.
 * See unioned types for specific parameters.
 */
export type ExportDestination =
  | SlackMessageUpdateDestination
  | MongoDestination;

/**
 * Used to send an update to a slack message
 */
export type SlackMessageUpdateDestination = {
  type: "slackMessageUpdate";
  responseUrl: string;
  userId: string; // slack user id
};

/**
 * Used to update an already populated mongo document
 */
export type MongoDestination = {
  type: "mongoDestination";
  exportId: string;
  userId: string; // slack user id
};

/**
 * Data model for a subscription to a data export.
 * This model is used to schedule delivery of a
 * csv file for a report.
 */
export interface DataExportSubscription {
  _id: string;
  createdAt: Date;
  active: boolean;
  orgId: string;
  lastDeliveredAt?: Date;
  lastAttemptedDelivery?: Date;
  nextDeliveryTime: Date;
  originalScheduleTime: Date;
  timezone: string;
  frequency: Cadence;
  destination: ExportDestination;
  reportParams: ReportParams;
}

export type SlashCommandLookupType =
  | "poll"
  | "track"
  | "survey"
  | "surveyTrack";

export type SlashCommandType =
  | "results"
  | "share"
  | "remind"
  | "close"
  | "reopen"
  | "delete"
  | "edit"
  | "export";

export interface SlashCommand {
  type: string;
  eventName: string;
  descriptionText: string;
  searchBarAction: string;
  longDescription?: string;
  feature?: string;
}

export interface PaywallFeatureCopy {
  shortDescription: string;
  longDescription: string;
  helpUrl?: string;
}

// Temporarily this can only be type=text but will allow for number, and date later
export interface PopulationSnapshotAttribute {
  type: "text" | "ignore";
  name: string;
}
export interface PopulationSnapshotUser {
  userId: string;
  slackUserId: string;
  [x: string]: any;
}

export interface PopulationSnapshot {
  _id: string;
  createdAt: Date;
  validUntil: Date;
  orgId: string;
  ownerId: string;
  filename: string;
  deleted: boolean;
  attributes: PopulationSnapshotAttribute[];
  population: PopulationSnapshotUser[];
}

/** Scopes that can be granted to a token in the Polly Web API */
export enum ApiScopes {
  pollsRead = "polls:read",
  pollsWrite = "polls:write",
  surveysRead = "surveys:read",
  surveysWrite = "surveys:write",
  templatesRead = "templates:read",
  templatesWrite = "templates:write",
  workflowsRead = "workflows:read",
  workflowsWrite = "workflows:write",
}

export const API_SCOPES = [
  ApiScopes.pollsRead,
  ApiScopes.pollsWrite,
  ApiScopes.surveysRead,
  ApiScopes.surveysWrite,
  ApiScopes.templatesRead,
  ApiScopes.templatesWrite,
  ApiScopes.workflowsRead,
  ApiScopes.workflowsWrite,
];

export enum ApiDataAccess {
  user = "user",
  organization = "organization",
}

/** Mongo Schema for the PollyApiToken */
export interface PollyApiToken {
  _id: string;
  token: string;
  refreshToken: string;
  issuedOn: Date;
  expiresOn?: Date;
  orgId: string;
  userId: string;
  dataAccess: ApiDataAccess;
  description: string;
  scopes: ApiScopes[];
  lastUsedAt?: Date;
}

// The types we allow to be supplied as custom parameter values for the API
export type ApiParameterValue = boolean | string | number | Date | string[];

export interface CustomMetadata {
  [key: string]: ApiParameterValue;
}

export interface EnterpriseGrid {
  _id: string; // internal mongo id
  // The name that the org has picked for the bot, which can be customized in settings
  slackBotName?: string;
  // The bot user's id in this EG
  slackBotUserId: string;
  // The API token to do all the things in Slack
  encryptedSlackBotToken: string;
  slackEnterpriseId: string;
  slackEnterpriseName: string;
  // Whether or not the Slack API token is still active
  slackBotTokenActive: boolean;
  // Scopes that the organization's bot token has
  scopes: string[];
  // version support for ease of data migration/handling
  version: number;
  createdAt: Date;
  // Settings for enterprise grid
  settings?: {
    optIns?: {
      marketing?: boolean;
    };
  };
}

export type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

/** Combine types `A` and `B`, but replace all conflicts with typings from `B` */
export type Override<A, B> = Omit<A, keyof B> & B;

export interface ActivityLog {
  _id: string;
  createdAt: Date;
  event: ActivityEvents;
  orgId: string;
  resourceData?: Object;
  resourceId: string;
  resourceName: string;
  resourceType: ResourceTypes;
  userId: string;
  userName: string;
  userPlatformId: string;
}

export enum ActivityEvents {
  CREATE = "create",
  DELETE = "delete",
  UPDATE = "update",
  EXPORT = "export",
  SENT = "sent",
}

export enum ResourceTypes {
  ORGANIZATION = "organization",
  POLL = "poll",
  SURVEY = "survey",
  TEMPLATE = "template",
  USER = "user",
  WORKFLOW = "workflow",
  SUBSCRIPTION = "subscription",
  TRACK = "track",
  QUIZ = "quiz",
  MESSAGE = "message",
  TRIVIA = "trivia",
  QNA = "qna",
}

export enum SurveyResultsFilterOptions {
  QUESTION = "question",
  PARTICIPANT = "participant",
}

export type PollysListFilter =
  | "user_created"
  | "user_participated"
  | "user_needs_to_respond";
export interface PollysListItem {
  id: string;
  title: string | JSXSlack.JSX.Element;
  pollyContext: JSXSlack.JSX.Element;
  overflow: JSXSlack.JSX.Element;
  type: "poll" | "survey" | "trivia" | "qna" | "quiz";
}

export enum SlackWorkflowBuilderNoopErrors {
  START_TRIAL_NOOP_ERROR = "START_TRIAL_NOOP_ERROR",
  UPGRADE_NOOP_ERROR = "UPGRADE_NOOP_ERROR",
  CHOOSE_TEMPLATE_NOOP_ERROR = "CHOOSE_TEMPLATE_NOOP_ERROR",
}

export interface TemplateListItem {
  templateId: string;
  title: string;
  totalQuestions: number;
  type: string;
  createdAt?: Date;
}

export interface PreviewTemplate {
  _id: string;
  title: string;
  results: string;
  anonymityLevel: string;
  hasRecurrenceSettings: boolean;
  rotating: boolean;
  questions: PreviewTemplateQuestion[];
}

export interface PreviewTemplateQuestion {
  type: string;
  title: string;
  questionOrder: number;
  isRequired: boolean;
  totalOptions?: number;
}

export interface DemoCTAMetadata {
  isCTAEnabled?: boolean;
  user?: { email: string; firstName?: string; lastName?: string };
  text?: string;
  anchorTagIcon?: string;
}

export interface WebBannerMetadata {
  isBannerEnabled: boolean;
  imgUrl: string;
  line1Text: string;
  line2Text: string;
  ctaText: string;
  ctaLink: string;
}

export interface PostCreateModalMetadata {
  isEnabled?: boolean;
  imgUrl?: string;
  context?: string;
}

export interface LifeCycleCTAMetadata {
  isEnabled?: boolean;
  headerText?: string;
  bodyText?: string;
  ctaText?: string;
  ctaLink?: string;
  imgUrl?: string;
}

export interface AppHomeFooterLink {
  iconUrl: string;
  text: string;
  url: string;
}

export interface AppHomeFooterLinksMetadata {
  links: AppHomeFooterLink[];
}

export interface RotatingTrackPollHeader {
  title: string;
  appeal?: string;
}

export type QnaAuthoringMode = "quick" | "detailed";
export type LinkScope = "none" | "public" | "workspace";
export type QnaSubmissionsTarget = SlackChannelTargetResults;

export type QnaCreateModel = {
  allowResponseReactions: boolean;
  allowDownvoting: boolean;
  allowAnonymous: boolean;
  approveQuestions: boolean;
  closeAt?: Date;
  description?: string;
  externalIdentifier?: string;
  openAt?: Date;
  sendNow: boolean;
  title: string;
  senderAlias?: string;
  allowReplies: boolean;
  collabortors?: string[];
  isScheduled: boolean;
  durationNumber?: number;
  durationUnit?: DurationUnit;
  channelId?: string;
  authoringMode: QnaAuthoringMode;
  webLinkScope: LinkScope;
  shortCodeId: ShortCodeId | undefined;
  qrCodeSettings: QRCodeRoutingType;
  joinCode?: JoinCode;
  submissionsTarget: QnaSubmissionsTarget;
  enableSlackMentions: boolean;
  enableResponseNotifications: boolean;
  eventId?: string;
};

export enum DMCampaignType {
  DMCampaign_v1 = "2025-05-campaign1",
  DMCampaign_v2 = "2025-05-campaign2",
  DMCampaign_v3 = "2025-06-campaign3",
  DMCampaign_v4 = "2025-06-campaign4",
  DMCampaign_v5 = "2025-06-campaign5",
  DMCampaign_v6 = "2025-06-campaign6",
  DMCampaign_v7 = "2025-06-campaign7",
  DMCampaign_v8_1 = "2025-07-campaign8-1", // zoom webinar registration
  DMCampaign_v8_2 = "2025-07-campaign8-2", // zoom webinar follow up
  DMCampaignShared_v1 = "DMCampaignShared_v1",
  DMCampaign_v9 = "campaign9",
  DMCampaign_v10 = "campaign10",
  DMCampaign_v11 = "campaign11",
  DMCampaign_v12 = "campaign12",
  DMCampaign_v13 = "campaign13",
  DMCampaign_v14 = "campaign14",
  DMCampaign_v15 = "campaign15",
  DMCampaign_v16 = "campaign16",
  DMCampaign_v17 = "campaign17",
  DMCampaign_v18 = "campaign18",
  DMCampaign_v19 = "campaign19",
  DMCampaign_v20 = "campaign20",
  DMCampaign_v21 = "campaign21",
  DMCampaign_v22 = "campaign22",
  DMCampaign_v23 = "campaign23",
  DMCampaign_v24 = "campaign24",
  DMCampaign_v25 = "campaign25",

  // Sequence 01 - Marketing DM Sequence with 5 DMs
  // Arm A: Creators, Arm B: Non-Creators
  Sequence01_01A = "sequence01-01A", // Sequence 1, DM 1, Creators
  Sequence01_01B = "sequence01-01B", // Sequence 1, DM 1, Non-Creators
  Sequence01_02A = "sequence01-02A", // Sequence 1, DM 2, Creators
  Sequence01_02B = "sequence01-02B", // Sequence 1, DM 2, Non-Creators
  Sequence01_03A = "sequence01-03A", // Sequence 1, DM 3, Creators
  Sequence01_03B = "sequence01-03B", // Sequence 1, DM 3, Non-Creators
  Sequence01_04A = "sequence01-04A", // Sequence 1, DM 4, Creators
  Sequence01_04B = "sequence01-04B", // Sequence 1, DM 4, Non-Creators
  Sequence01_05A = "sequence01-05A", // Sequence 1, DM 5, Creators
  Sequence01_05B = "sequence01-05B", // Sequence 1, DM 5, Non-Creators

  // Sequence 02 - Marketing DM Sequence with 5 DMs
  // Arm A: Creators, Arm B: Non-Creators
  Sequence02_01A = "sequence02-01A", // Sequence 2, DM 1, Creators
  Sequence02_01B = "sequence02-01B", // Sequence 2, DM 1, Non-Creators
  Sequence02_02A = "sequence02-02A", // Sequence 2, DM 2, Creators
  Sequence02_02B = "sequence02-02B", // Sequence 2, DM 2, Non-Creators
  Sequence02_03A = "sequence02-03A", // Sequence 2, DM 3, Creators
  Sequence02_03B = "sequence02-03B", // Sequence 2, DM 3, Non-Creators
  Sequence02_04A = "sequence02-04A", // Sequence 2, DM 4, Creators
  Sequence02_04B = "sequence02-04B", // Sequence 2, DM 4, Non-Creators
  Sequence02_05A = "sequence02-05A", // Sequence 2, DM 5, Creators
  Sequence02_05B = "sequence02-05B", // Sequence 2, DM 5, Non-Creators

  // Sequence 03 - Marketing DM Sequence with 5 DMs
  // Arm A: Creators, Arm B: Non-Creators
  Sequence03_01A = "sequence03-01A", // Sequence 3, DM 1, Creators
  Sequence03_01B = "sequence03-01B", // Sequence 3, DM 1, Non-Creators
  Sequence03_02A = "sequence03-02A", // Sequence 3, DM 2, Creators
  Sequence03_02B = "sequence03-02B", // Sequence 3, DM 2, Non-Creators
  Sequence03_03A = "sequence03-03A", // Sequence 3, DM 3, Creators
  Sequence03_03B = "sequence03-03B", // Sequence 3, DM 3, Non-Creators
  Sequence03_04A = "sequence03-04A", // Sequence 3, DM 4, Creators
  Sequence03_04B = "sequence03-04B", // Sequence 3, DM 4, Non-Creators
  Sequence03_05A = "sequence03-05A", // Sequence 3, DM 5, Creators
  Sequence03_05B = "sequence03-05B", // Sequence 3, DM 5, Non-Creators

  // Sequence 04 - Marketing DM Sequence with 5 DMs
  // Arm A: Creators, Arm B: Non-Creators
  Sequence04_01A = "sequence04-01A", // Sequence 4, DM 1, Creators
  Sequence04_01B = "sequence04-01B", // Sequence 4, DM 1, Non-Creators
  Sequence04_02A = "sequence04-02A", // Sequence 4, DM 2, Creators
  Sequence04_02B = "sequence04-02B", // Sequence 4, DM 2, Non-Creators
  Sequence04_03A = "sequence04-03A", // Sequence 4, DM 3, Creators
  Sequence04_03B = "sequence04-03B", // Sequence 4, DM 3, Non-Creators
  Sequence04_04A = "sequence04-04A", // Sequence 4, DM 4, Creators
  Sequence04_04B = "sequence04-04B", // Sequence 4, DM 4, Non-Creators
  Sequence04_05A = "sequence04-05A", // Sequence 4, DM 5, Creators
  Sequence04_05B = "sequence04-05B", // Sequence 4, DM 5, Non-Creators
}

export enum DMCampaignDropdownOptions {
  EXPLORE_TEMPLATES = "Explore Templates",
  I_HAVE_QUESTIONS = "I have questions",
  REMIND_ME_ABOUT_THIS_LATER = "Remind me about this later",
  SHARE_THIS_WITH_SOMEONE = "Share this with someone",
  TAKE_A_TOUR = "Take a product tour",
  SEE_PLANS = "See plans",
  SEE_DEMO = "See demo",
}

// --- Marketing Campaign Creator types ---

export enum MarketingCampaignStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export enum PrimaryCTAType {
  // Creation CTAs
  CreatePolly = "CreatePollyButton",
  CreateTrivia = "CreateTriviaButton",
  CreateTeamTrivia = "CreateTeamTriviaButton",
  CreateWordCloud = "CreateWordCloudButton",
  CreateQuiz = "CreateQuizButton",
  CreateQnA = "CreateQnAButton",
  CreateAMA = "CreateAMAButton",
  CreateSurvey = "CreateSurveyButton",
  CreatePulseSurvey = "CreatePulseSurveyButton",
  CreateHotTake = "CreateHotTakeButton",
  CreateIcebreaker = "CreateIcebreakerButton",
  // Opens the Pairings "Add Channel" (SetupChannel) modal — the same entry
  // point as the apphome "Add Channel" CTA. Entitlement + scopes are gated
  // server-side on Create submit (PS-5159/PS-5188): every workspace gets one
  // free channel, then subsequent channels are paywalled. Safe to surface to
  // any sub state — the gate runs downstream, not here at send time.
  CreatePairings = "CreatePairings",
  // Opens a new presentation in the web dashboard through the login
  // interstitial (/presentations/new). Plan walls stay the platform's
  // (authoring is free; presenting, sharing and exporting are gated), but the
  // webapp only routes /presentations for users in the Feature_Events_GA
  // rollout and sends everyone else to the dashboard home (PS-5765).
  CreatePresentation = "CreatePresentation",
  // Conversion CTAs
  StartTrial = "StartTrialButton",
  UpgradeNow = "UpgradeNowButton",
  SeePlans = "SeePlansButton",
  // Sends the user to the webapp Pairings upgrade funnel
  // (/upgrade?source=pairings) via the web-dashboard login interstitial, so no
  // re-login is needed. Free users land on the Pairings upsell; Basic/Pro/Team
  // go straight to the prorated add-on payment page (PS-5189).
  GetPairings = "GetPairings",
  WatchADemo = "WatchADemoButton",
  ZoomWebinarRegistration = "ZoomWebinarRegistrationButton",
  // Navigation CTAs
  LinkButton = "LinkButton",
  ExploreTemplates = "ExploreTemplates",
  TakeProductTour = "TakeProductTour",
  VisitWebApp = "VisitWebApp",
  // Direct-button entry point to the same ContactSalesModal opened by the
  // "I have questions" dropdown option — same modal, same event properties
  // (PS-5579).
  ContactUs = "MarketingContactUsButton",
}

export interface MarketingCampaignAsset {
  url: string;
  altText: string;
}

export interface MarketingCampaignCTA {
  type: PrimaryCTAType;
  label: string;
  url?: string;
}

export interface MarketingCampaignSecondaryCTA {
  mode: "dropdown" | "button";
  dropdownOptions?: DMCampaignDropdownOptions[];
  button?: MarketingCampaignCTA;
}

export interface MarketingCampaignDoc {
  _id: string;
  identifier: string;
  status: MarketingCampaignStatus;
  headerText: string;
  bodyMrkdwn: string;
  asset?: MarketingCampaignAsset;
  primaryCTA: MarketingCampaignCTA;
  secondaryCTA?: MarketingCampaignSecondaryCTA;
  /**
   * Video sent in the follow-up DM when the recipient clicks a Watch A Demo
   * button or picks "See demo" from the dropdown. Without it they get the
   * default "Make your next polly pop" video (PS-5765).
   */
  demoUrl?: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// --- Marketing Sequence Builder types (PS-5765) ---

export enum MarketingSequenceStatus {
  ACTIVE = "active",
  PAUSED = "paused",
  STOPPED = "stopped",
}

export interface MarketingSequenceStep {
  /** _id of a Campaign Builder campaign, so renaming a draft can't orphan it */
  campaignId: string;
}

export interface MarketingSequenceDoc {
  _id: string;
  title: string;
  /** DM1, DM2, ... in send order */
  steps: MarketingSequenceStep[];
  /** Days between DMs; a DM landing on a weekend moves to the Monday */
  cadenceDays: number;
  status: MarketingSequenceStatus;
  /** Set on the first enrollment; from then on edits apply to enrollees */
  firstEnrolledAt?: Date;
  /**
   * Bumped by each change to the DMs or cadence. Written only with $inc
   * (recordSequenceEdit), never by a model save.
   */
  editVersion?: number;
  pausedAt?: Date;
  stoppedAt?: Date;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface DMCampaignMessage {
  messageName: DMCampaignType | string;
  sendDate: Date;
  messageId?: string;
  isDeleted?: boolean;
}

export enum DMCampaignReminderDuration {
  TWO_DAYS = "2 days",
  ONE_WEEK = "1 week",
  TWO_WEEKS = "2 weeks",
}

export const enum GSheetSyncInterval {
  HOURLY = "hourly",
  DAILY = "daily",
  WEEKLY = "weekly",
}

export interface DMCampaign {
  active: boolean;
  /** Type of the DM campaign (enum value for legacy, string identifier for dynamic campaigns) */
  type: DMCampaignType | string;
  sendDate: Date;
  messages: DMCampaignMessage[];
  /** Whether the campaign is still valid (within 24 hours of send date) */
  isValid: boolean;
  /** whether the message has been rescheduled */
  rescheduled?: {
    date: Date;
    noOfDays: number;
  };
  /** reassignment information if the message was reassigned */
  reassigned?: {
    /** User ID it was reassigned to */
    toUser?: string;
    /** User ID it was reassigned from */
    fromUser?: string;
    /** Date when it was reassigned */
    date: Date;
  };
  /**
   * If true, skip the paid plan unenrollment check for this DM.
   * Used when CS schedules a sequence DM individually (not as part of a full sequence enrollment).
   */
  bypassPaidPlanCheck?: boolean;
  /** Set when this DM was scheduled by a Sequence Builder enrollment (PS-5765) */
  marketingSequenceId?: string;
  /** 0-based position of this DM in that sequence */
  marketingSequenceStep?: number;
  /** The sequence's editVersion this DM was built from */
  marketingSequenceVersion?: number;
}

/**
 * Result interface for batch DM campaign creation
 */
export interface DMCampaignAddResult {
  totalProcessed: number;
  alreadyExists: number;
  alreadyExistsUserIds: string[];
  newlyAdded: number;
  notFound: number;
  notFoundUserIds: string[];
}

/**
 * Result interface for batch DM campaign message deletion
 */
export interface DMCampaignDeleteResult {
  totalProcessed: number;
  messagesDeleted: number;
  usersProcessed: number;
  notFound: number;
  notFoundUserIds: string[];
  failedDeletions: number;
  failedUserIds: string[];
}

export type ResultsFilteringState = {
  blockType?: "full" | "partial";
  blockedResponsesCount?: number;
  responseLimitHitDate?: Date;
};

export type PollyCountQueryResult = {
  _id: {
    year: string;
    month: string;
  };
  count: number;
}[];

export interface TimeChartData {
  date: Date;
  value: number;
  instanceResponses?: number;
}

export type GetUsageResult = {
  chartData: TimeChartData[];
  average: number;
  peak: number;
};

export type WebVotingUrlAndTarget = {
  url: string;
  target: PlatformPollyWebLinkTargetSchema;
};

export type ShareableResource =
  | Resource.Posts
  | Resource.Surveys
  | Resource.Tracks
  | Resource.OrgTemplates
  | Resource.Workflows
  | Resource.Quizzes
  | Resource.Qnas;

export enum PermissionChoiceType {
  NONE = "NONE",
  VIEW = "VIEW",
  MANAGE = "MANAGE",
}

export function getPlatformPollyQuestionType(
  polly: PlatformPollySchema,
):
  | "wordCloud"
  | "yesNo"
  | "qna"
  | "unknown"
  | "multiple-choice"
  | "nominal"
  | "ordinal"
  | "point-allocation"
  | "rank-order"
  | "nominal-image"
  | PollType {
  if (polly.type === "qna") {
    return "qna";
  }
  if (polly.questions.length === 1) {
    const question = polly.questions[0];

    if (question.type === "open-ended") {
      if (
        (question as unknown as OpenEndedQuestion)?.analysisSettings?.[0]
          ?.type === "word-cloud"
      ) {
        return "wordCloud";
      }
      return PollType.OPEN_ENDED;
    }

    if (question.type === "nominal") {
      const nominalQuestion = question as unknown as NominalQuestion;

      // Check for yes/no format
      if ((polly as any).formatType === "yes-no") {
        return "yesNo";
      }

      if (!nominalQuestion.maximumChoices) {
        return "multiple-choice";
      }
    }

    if (question.type === "ordinal") {
      const ordinalQuestion = question as unknown as OrdinalQuestion;
      const choices = ordinalQuestion.choices;

      if (choices.length === 5) {
        if (choices[0].label === "1") {
          return PollType.ONE_TO_FIVE;
        }
        if (choices[0].label === "Strongly Disagree") {
          return PollType.FIVE_POINT;
        }
        if (choices[0].label.includes("Awful")) {
          return PollType.RANK_FIVE_EMOJI;
        }
      }

      if (choices.length === 10) {
        return PollType.ONE_TO_TEN;
      }

      if (choices.length > 10) {
        return PollType.NPS;
      }
    }

    return question.type;
  }

  return "unknown";
}
